import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShoppingCart, 
  Trash2, 
  Plus, 
  Minus, 
  ArrowLeft, 
  ArrowRight,
  CreditCard,
  Package,
  Truck,
  CheckCircle
} from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import StyledButton from '../components/ui/StyledButton';
import LocalNotification from '../components/ui/LocalNotification';
import ConfirmModal from '../components/ui/ConfirmModal';

const CartPage = () => {
  const { 
    cartItems, 
    loading, 
    updateCartItem, 
    removeFromCart, 
    clearCart, 
    getCartTotal, 
    getCartItemCount 
  } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [updatingItem, setUpdatingItem] = useState(null);
  const [notifications, setNotifications] = useState({});
  const [confirmClear, setConfirmClear] = useState(false);

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    
    setUpdatingItem(itemId);
    try {
      await updateCartItem(itemId, newQuantity);
    } finally {
      setUpdatingItem(null);
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await removeFromCart(itemId);
      setNotifications(prev => ({
        ...prev,
        [itemId]: {
          show: true,
          message: 'Article supprimé du panier avec succès',
          type: 'success'
        }
      }));
    } catch (error) {
      console.error('Error removing item:', error);
      setNotifications(prev => ({
        ...prev,
        [itemId]: {
          show: true,
          message: 'Erreur lors de la suppression',
          type: 'error'
        }
      }));
    }
  };

  const handleClearCart = async () => {
    try {
      await clearCart();
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate('/checkout');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-iranian-500 mx-auto"></div>
          <p className="text-white mt-4">Chargement du panier...</p>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center">
                <ShoppingCart className="h-12 w-12 text-gray-400" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">
              Votre panier est vide
            </h1>
            <p className="text-gray-300 text-lg mb-8">
              Découvrez nos magnifiques bijoux et commencez à faire vos achats
            </p>
            <div className="space-x-4">
              <Link
                to="/products"
                className="inline-flex items-center px-6 py-3 bg-iranian-600 text-white rounded-lg hover:bg-iranian-700 transition-colors"
              >
                <Package className="h-5 w-5 mr-2" />
                Voir nos produits
              </Link>
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Retour à l'accueil
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gray-900 py-12 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Votre Panier
            </h1>
            <p className="text-gray-300">
              {getCartItemCount()} article{getCartItemCount() > 1 ? 's' : ''} • Total: {formatPrice(getCartTotal())}
            </p>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => setConfirmClear(true)}
              className="px-4 py-2 text-red-400 hover:text-red-300 transition-colors flex items-center"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Vider le panier
            </button>
            <Link
              to="/products"
              className="px-4 py-2 text-iranian-400 hover:text-iranian-300 transition-colors flex items-center"
            >
              <Package className="h-4 w-4 mr-2" />
              Continuer les achats
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-gray-800 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">
                Articles ({cartItems.length})
              </h2>
              
              <div className="space-y-4">
                <AnimatePresence>
                  {cartItems.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="flex items-center space-x-4 p-4 bg-gray-700 rounded-lg"
                    >
                      {/* Product Image */}
                      <div className="flex-shrink-0">
                        <div 
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => navigate(`/products/${item.product_id}`)}
                        >
                          <img
                            src={item.primary_image || '/placeholder-product.svg'}
                            alt={item.name_fr}
                            className="w-20 h-20 object-cover rounded-lg hover:scale-105 transition-transform"
                            onError={(e) => {
                              console.log('Image error for item:', item.id, 'URL:', item.primary_image);
                              e.target.src = '/placeholder-product.svg';
                            }}
                          />
                        </div>
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <div 
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => navigate(`/products/${item.product_id}`)}
                        >
                          <h3 className="text-white font-medium truncate hover:text-iranian-300 transition-colors">
                            {item.name_fr}
                          </h3>
                          <p className="text-gray-400 text-sm">
                            Référence: #{item.product_id}
                          </p>
                          <div className="flex items-center mt-2">
                            <span className="text-iranian-400 font-semibold">
                              {formatPrice(item.sale_price || item.price)}
                            </span>
                            {item.sale_price && (
                              <span className="text-gray-500 line-through ml-2">
                                {formatPrice(item.price)}
                              </span>
                            )}
                          </div>
                          {/* Stock available */}
                          {typeof item.in_stock !== 'undefined' && (
                            <div className="mt-1">
                              <span className={`text-sm ${item.in_stock > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {item.in_stock > 0 ? `Stock: ${item.in_stock}` : 'Rupture'}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* View Details Button */}
                        <button
                          onClick={() => navigate(`/products/${item.product_id}`)}
                          className="mt-3 flex items-center text-sm text-iranian-400 hover:text-iranian-300 transition-colors"
                        >
                          <ArrowRight className="h-4 w-4 mr-1" />
                          Voir les détails et tailles
                        </button>
                      </div>

                      {/* Quantity Controls */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          disabled={updatingItem === item.id}
                          className="p-1 rounded-full bg-gray-600 hover:bg-gray-500 text-white disabled:opacity-50"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        
                        <span className="text-white font-medium min-w-[2rem] text-center">
                          {updatingItem === item.id ? '...' : item.quantity}
                        </span>
                        
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          disabled={updatingItem === item.id}
                          className="p-1 rounded-full bg-gray-600 hover:bg-gray-500 text-white disabled:opacity-50"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Total Price */}
                      <div className="text-right min-w-[6rem]">
                        <div className="text-white font-semibold">
                          {formatPrice((item.sale_price || item.price) * item.quantity)}
                        </div>
                      </div>

                      {/* Remove Button */}
                      <div className="relative">
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="p-2 text-red-400 hover:text-red-300 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                        
                        {/* Local Notification */}
                        <LocalNotification
                          message={notifications[item.id]?.message || ''}
                          type={notifications[item.id]?.type || 'success'}
                          isVisible={notifications[item.id]?.show || false}
                          onClose={() => setNotifications(prev => ({
                            ...prev,
                            [item.id]: { ...prev[item.id], show: false }
                          }))}
                          position="bottom"
                        />
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-xl p-6 sticky top-6">
              <h2 className="text-xl font-semibold text-white mb-6">
                Résumé de la commande
              </h2>

              {/* Summary Details */}
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-300">
                  <span>Sous-total ({getCartItemCount()} article{getCartItemCount() > 1 ? 's' : ''})</span>
                  <span>{formatPrice(getCartTotal())}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Livraison</span>
                  <span className="text-green-400">Gratuite</span>
                </div>
                <hr className="border-gray-600" />
                <div className="flex justify-between text-white text-lg font-semibold">
                  <span>Total</span>
                  <span>{formatPrice(getCartTotal())}</span>
                </div>
              </div>

              {/* Checkout Button */}
              <StyledButton
                onClick={handleCheckout}
                fullWidth
                size="lg"
                icon={CreditCard}
                variant="primary"
                className="mb-4"
              >
                {isAuthenticated ? 'Procéder au paiement' : 'Se connecter pour continuer'}
              </StyledButton>

              
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Confirm Clear Cart Modal */}
    <ConfirmModal
      isOpen={confirmClear}
      title="Vider le panier"
      message="Êtes-vous sûr de vouloir vider votre panier ? Cette action est irréversible."
      confirmText="Vider"
      cancelText="Annuler"
      danger
      onCancel={() => setConfirmClear(false)}
      onConfirm={async () => {
        setConfirmClear(false);
        await handleClearCart();
      }}
    />
    </>
  );
};

export default CartPage; 